import Cookies from 'js-cookie';

const BUILD_ID_KEY = 'app_build_id';
const DEPLOY_RELOAD_KEY = 'app_deploy_reload';

/**
 * Returns the build ID baked in at compile time by rsbuild.
 * Each new production build gets a unique value (timestamp-based).
 */
export function getBuildId() {
  return import.meta.env.PUBLIC_BUILD_ID || '';
}

/**
 * Compares the build ID stored in localStorage against the one
 * baked into the current bundle.
 *
 * Returns true when a new deployment is detected (stored ID differs
 * from current bundle ID). Always updates localStorage to the current
 * build ID so subsequent loads do not re-trigger the check.
 */
export function checkForNewDeployment() {
  const currentBuildId = getBuildId();
  if (!currentBuildId) return false;

  const storedBuildId = localStorage.getItem(BUILD_ID_KEY);

  // Persist current ID immediately — if we're about to reload, the
  // new load will find this value and skip the deployment check.
  localStorage.setItem(BUILD_ID_KEY, currentBuildId);

  // Only flag as "new deployment" when there was a *previous* value
  // that differs from the current bundle.
  return storedBuildId !== null && storedBuildId !== currentBuildId;
}

/**
 * Clears all browser-side session state that could cause a stale
 * half-authenticated state after a new deployment:
 *  - All localStorage (MSAL token cache, app state, etc.)
 *  - All js-cookie cookies
 *
 * Preserves the current build ID in localStorage so the next load
 * does not incorrectly trigger another deployment-reload cycle.
 */
export function clearStaleSessionState() {
  const currentBuildId = getBuildId();

  // Wipe everything — including any MSAL token-cache entries
  localStorage.clear();

  // Re-write the build ID so the next load doesn't re-trigger
  if (currentBuildId) {
    localStorage.setItem(BUILD_ID_KEY, currentBuildId);
  }

  // Clear all cookies (auth tokens, isAuthenticated flag, etc.)
  Object.keys(Cookies.get()).forEach((name) => Cookies.remove(name));
}

/**
 * Records in sessionStorage that we already performed a deploy-reload
 * in this browser tab.  sessionStorage survives a location.reload()
 * but is cleared when the tab is closed, so it cannot accumulate.
 */
export function markDeployReloadDone() {
  try {
    sessionStorage.setItem(DEPLOY_RELOAD_KEY, '1');
  } catch {
    // ignore — private-browsing quota errors, etc.
  }
}

/**
 * Returns true if we already reloaded once for a new deployment in
 * this tab session.  Used to prevent infinite reload loops in edge
 * cases where the new bundle is not yet cached by the browser.
 */
export function hasAlreadyReloadedForDeploy() {
  return sessionStorage.getItem(DEPLOY_RELOAD_KEY) === '1';
}
